/**
 * Bundle 131 + 132 - dedicated modal bodies for the incident
 * tiles that were missing on prod (P0-1 regression).
 *
 * Uses the shared EventPicker from Bundle130OrphanFlows so every
 * event_id input across incidents is a dropdown (P0-2 invariant).
 *
 * Hygiene: no em/en dashes, no eslint-disable.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Beaker, Play } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

// Local event picker: Bundle 133 correction - uses the broad
// /admin/incident-event-picker endpoint which lists EVERY
// published event (not just those with submitted responses).
// Renders a per-incident badge count next to each option so
// operators can see at a glance which events actually need
// attention without excluding zero-count events.
const EventPicker = ({ value, onChange, testId, scope }) => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    const url = scope
      ? `/admin/incident-event-picker?scope=${encodeURIComponent(scope)}`
      : '/admin/incident-event-picker';
    api.get(url)
      .then(({ data }) => { setEvents(data.events || []); })
      .catch(() => setEvents([]));
  }, [scope]);
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger data-testid={testId || 'incident-event-picker'}>
        <SelectValue placeholder="Choose an event" />
      </SelectTrigger>
      <SelectContent>
        {events.length === 0 ? (
          <SelectItem value="_none" disabled>
            No published events found.
          </SelectItem>
        ) : events.map((e) => {
          const n = Number(e.badge_count || 0);
          const badgeClass = n > 0
            ? 'bg-amber-50 text-amber-800 border-amber-200'
            : 'bg-neutral-100 text-neutral-600 border-neutral-200';
          return (
            <SelectItem key={e.id} value={e.id}
                          data-testid={`incident-event-opt-${e.id}`}>
              <span className="inline-flex items-center gap-2">
                <span>
                  {e.title || e.id}
                  {' * '}
                  {(e.event_date || e.start_datetime || '').slice(0, 10)}
                </span>
                {scope ? (
                  <span className={
                    'text-[10px] rounded border px-1.5 py-0.5 '
                    + badgeClass
                  }
                        data-testid={`incident-event-badge-${e.id}`}>
                    {n} affected
                  </span>
                ) : null}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

const EventScopedFlow = ({ endpoint, testIdPrefix, extraCopy, scope }) => {
  const [eventId, setEventId] = useState('');
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmApply, setConfirmApply] = useState(false);

  const call = async (dry) => {
    if (!eventId) { toast.error('Pick an event.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, {
        event_id: eventId, dry_run: dry,
      });
      if (dry) { setDryRun(data); setApplyResult(null); }
      else { setApplyResult(data); setConfirmApply(false); }
      toast.success(dry ? 'Dry-run complete.' : 'Applied.');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || (dry ? 'Dry-run failed.' : 'Apply failed.'));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3"
          data-testid={`${testIdPrefix}-body`}>
      <div className="space-y-1">
        <label className="text-xs uppercase tracking-widest text-neutral-500">
          Event *
        </label>
        <EventPicker
          value={eventId}
          onChange={(v) => { setEventId(v);
            setDryRun(null); setApplyResult(null); }}
          testId={`${testIdPrefix}-event-picker`}
          scope={scope}
        />
      </div>
      {extraCopy ? (
        <div className="text-[11px] text-neutral-600 rounded border border-neutral-200 bg-neutral-50 p-2">
          {extraCopy}
        </div>
      ) : null}
      {dryRun ? (
        <div className="rounded border border-primary-100 bg-primary-50/40 p-3"
              data-testid={`${testIdPrefix}-dry-run`}>
          <div className="text-[10px] uppercase tracking-widest text-primary-800 mb-1">
            Dry-run result
          </div>
          <pre className="text-[11px] font-mono text-primary-900 whitespace-pre-wrap max-h-56 overflow-auto">
            {JSON.stringify(dryRun, null, 2)}
          </pre>
        </div>
      ) : null}
      {applyResult ? (
        <div className="rounded border border-emerald-200 bg-emerald-50/60 p-3"
              data-testid={`${testIdPrefix}-apply-result`}>
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 mb-1">
            Apply result
          </div>
          <pre className="text-[11px] font-mono text-primary-900 whitespace-pre-wrap max-h-56 overflow-auto">
            {JSON.stringify(applyResult, null, 2)}
          </pre>
        </div>
      ) : null}
      <div className="flex gap-2 justify-end pt-1">
        <Button
          variant="outline"
          disabled={busy || !eventId}
          onClick={() => call(true)}
          data-testid={`${testIdPrefix}-dry-run-btn`}
        >
          <Beaker className="h-4 w-4 mr-1.5" /> Dry-run
        </Button>
        {!confirmApply ? (
          <Button
            disabled={busy || !eventId || !dryRun}
            onClick={() => setConfirmApply(true)}
            className="bg-primary-800 text-white hover:bg-primary-900"
            data-testid={`${testIdPrefix}-apply-btn`}
          >
            <Play className="h-4 w-4 mr-1.5" /> Apply
          </Button>
        ) : (
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-2 py-1"
                data-testid={`${testIdPrefix}-confirm-inline`}>
            <span className="text-[11px] text-red-800">
              Confirm apply?
            </span>
            <Button size="sm" variant="outline"
                    onClick={() => setConfirmApply(false)}
                    data-testid={`${testIdPrefix}-confirm-cancel`}>
              No
            </Button>
            <Button size="sm"
                    onClick={() => call(false)}
                    className="bg-red-600 text-white hover:bg-red-700"
                    data-testid={`${testIdPrefix}-confirm-yes`}>
              Yes, apply
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const Bundle131ResurrectHiddenBlocksBody = () => (
  <EventScopedFlow
    endpoint="/admin/incident/resurrect-hidden-blocks-with-content"
    testIdPrefix="bundle131-resurrect"
    scope="resurrect_hidden_blocks"
    extraCopy={
      'For events (Philly-shape) where a block was edited via VBE '
      + 'but never explicitly unhidden. Any block with hidden=true '
      + 'AND non-empty content is flipped to hidden=false. Idempotent.'
    }
  />
);

export const Bundle131BackfillTestimonialLastNamesBody = () => (
  <EventScopedFlow
    endpoint="/admin/incident/backfill-testimonial-last-names"
    testIdPrefix="bundle131-testibackfill"
    scope="backfill_testimonial_last_names"
    extraCopy={
      'For every testimonial_consent row missing a reporter_last_name '
      + 'on the event, cross-reference the registration + users '
      + 'collection by email. Rebuilds display_name via '
      + 'format_initials when attribution=initials.'
    }
  />
);

export const Bundle132RecomputeRatingCountsBody = () => (
  <EventScopedFlow
    endpoint="/admin/incident/recompute-rating-counts"
    testIdPrefix="bundle132-recomputerc"
    scope="recompute_rating_counts"
    extraCopy={
      'Fix for stale rows created before Bundle 131 finalizer fix '
      + '(Alina Counts=blank symptom). Recomputes '
      + 'rating_counts_toward_event via the same _current_is_attendee '
      + 'helper the normal submit path uses. Idempotent.'
    }
  />
);

// Bundle 140 P0-1 fix - shipping the tile that Bundle 136 registered
// in INCIDENT_REGISTRY but never wired into the panel. Same shape as
// Bundle 132 (event picker + dry-run/apply).
export const Bundle136HideLegacyTestimonialsBody = () => (
  <EventScopedFlow
    endpoint="/admin/incident/hide-legacy-testimonials"
    testIdPrefix="bundle136-hidelegacy"
    scope="hide_legacy_testimonials"
    extraCopy={
      'One-time sweep for orphan testimonial_consents (attribution='
      + 'initials or first_name whose parent survey response was '
      + 'never submitted). Bundle 136 P0-1 fix for the Dallas '
      + 'Testimonials-tab-shows-6-when-2-submitted regression.'
    }
  />
);
